import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { usePathParam } from 'react-resource-router';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { errorToastProps, infoToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';

import commonMessages from '../../common/messages';
import { type HeartbeatRequest } from '../../common/types';
import {
	createHeartbeatEndpoint,
	deleteHeartbeatEndpoint,
	switchHeartbeatEndpoint,
	updateHeartbeatEndpoint,
	validateHeartbeatNameEndpoint,
} from '../endpoints';

import messages from './messages';

export const useHeartbeatsService = () => {
	const [teamId] = usePathParam('teamId');
	const [validateLoading, setValidateLoading] = useState(false);
	const [isSwitchLoading, setSwitchLoading] = useState(false);
	const [isDeleting, setDeleting] = useState(false);

	const flagApi = useFlags();
	const { formatMessage } = useIntl();
	const defaultCatch = useDefaultCatch();

	const validateName = (heartbeatName: string) => {
		setValidateLoading(true);

		return validateHeartbeatNameEndpoint(heartbeatName, teamId)
			.catch(
				defaultCatch({
					id: 'useHeartbeatsService-validateHeartbeatName',
					httpErrorHandler: (err: any) => {
						if (err.response?.status > 499) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: formatMessage(commonMessages.errorTitle),
							});
						}
						throw err;
					},
				}),
			)
			.finally(() => {
				setValidateLoading(false);
			});
	};

	const create = (data: HeartbeatRequest) => {
		return createHeartbeatEndpoint(data, teamId).catch(
			defaultCatch({
				id: 'useHeartbeatsService-createHeartbeat',
				httpErrorHandler: (err: any) => {
					flagApi.showFlag({
						...errorToastProps(),
						description: err.message,
						title: formatMessage(messages.createErrorTitle),
					});

					throw err;
				},
			}),
		);
	};

	const edit = (data: HeartbeatRequest) => {
		return updateHeartbeatEndpoint(data, teamId)
			.then((res) => res)
			.catch(
				defaultCatch({
					id: 'useHeartbeatsService-updateHeartbeat',
					httpErrorHandler: (err: any) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.editErrorTitle),
						});

						throw err;
					},
				}),
			);
	};

	const switchHeartbeat = (enable: boolean, name: string) => {
		setSwitchLoading(true);

		return switchHeartbeatEndpoint(enable, name, teamId)
			.then((res) => {
				flagApi.showFlag({
					...successToastProps(),
					title: formatMessage(messages.switchSuccessTitle, { enable }),
				});

				return res;
			})
			.catch(
				defaultCatch({
					id: 'useHeartbeatsService-switchHeartbeat',
					httpErrorHandler: (err: any) => {
						flagApi.showFlag({
							...errorToastProps(),
							description: err.message,
							title: formatMessage(messages.switchErrorTitle, { enable }),
						});

						throw err;
					},
				}),
			)
			.finally(() => {
				setSwitchLoading(false);
			});
	};

	const deleteHeartbeat = useCallback(
		(name: string) => {
			setDeleting(true);

			return deleteHeartbeatEndpoint(name, teamId)
				.then(() => {
					flagApi.showFlag({
						...infoToastProps(),
						description: formatMessage(messages.deleteSuccessDescription),
						title: formatMessage(messages.deleteSuccessTitle, {
							heartbeatName: name,
						}),
					});
				})
				.catch((e) => {
					defaultCatch(
						{
							id: 'deleteHeartbeatAction',
							httpErrorHandler: (error) => {
								flagApi.showFlag({
									...errorToastProps(),
									description: error.message,
									title: formatMessage(messages.deleteErrorTitle),
								});
							},
						},
						e,
					);

					throw e;
				})
				.finally(() => {
					setDeleting(false);
				});
		},
		[defaultCatch, flagApi, formatMessage, teamId],
	);

	return {
		isDeleting,
		validateLoading,
		isSwitchLoading,
		validateName,
		create,
		edit,
		switchHeartbeat,
		deleteHeartbeat,
	};
};
